import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import MapboxDraw from '@mapbox/mapbox-gl-draw'
import { DrawingTools } from '../DrawingTools'
import { DrawingTool, DrawingData } from '../DrawingTools/DrawingTools.types'
import { MapDrawProps, DrawingMode } from './MapDraw.types'
import { HeightInputModal } from './HeightInputModal'
import { Icon } from '../../atoms/Icon'
import { useDrawMeasurement } from './useDrawMeasurement'
import { useMeasureDistance } from './useMeasureDistance'
import {
  calculateDistance,
  formatDistance,
  calculateMidpoint,
  getMeasurementSegments,
} from './MapDraw.utils'

// Calculate polygon area using the Shoelace formula (in square meters)
const calculatePolygonArea = (coordinates: [number, number][]): number => {
  if (coordinates.length < 3) return 0

  // Convert lat/lng to approximate meters using UTM-like projection
  const toMeters = (coord: [number, number]): [number, number] => {
    const [lng, lat] = coord
    // Approximate conversion (more accurate for small polygons)
    const metersPerDegreeLat = 111320
    const metersPerDegreeLng = 111320 * Math.cos((lat * Math.PI) / 180)
    return [lng * metersPerDegreeLng, lat * metersPerDegreeLat]
  }

  const meterCoords = coordinates.map(toMeters)

  // Shoelace formula
  let area = 0
  for (let i = 0; i < meterCoords.length; i++) {
    const j = (i + 1) % meterCoords.length
    area += meterCoords[i][0] * meterCoords[j][1]
    area -= meterCoords[j][0] * meterCoords[i][1]
  }

  return Math.abs(area) / 2
}

// Calculate polygon centroid
const calculatePolygonCentroid = (
  coordinates: [number, number][]
): [number, number] => {
  if (coordinates.length < 3) return coordinates[0] || [0, 0]

  let x = 0,
    y = 0
  let area = 0

  for (let i = 0; i < coordinates.length; i++) {
    const j = (i + 1) % coordinates.length
    const crossProduct =
      coordinates[i][0] * coordinates[j][1] -
      coordinates[j][0] * coordinates[i][1]
    area += crossProduct
    x += (coordinates[i][0] + coordinates[j][0]) * crossProduct
    y += (coordinates[i][1] + coordinates[j][1]) * crossProduct
  }

  area /= 2
  if (area === 0) {
    // Fallback to simple average if area calculation fails
    x =
      coordinates.reduce((sum, coord) => sum + coord[0], 0) / coordinates.length
    y =
      coordinates.reduce((sum, coord) => sum + coord[1], 0) / coordinates.length
  } else {
    x /= 6 * area
    y /= 6 * area
  }

  return [x, y]
}

// Format area for display
const formatArea = (squareMeters: number): string => {
  return `${squareMeters.toFixed(1)} m²`
}

// Format volume for display
const formatVolume = (cubicMeters: number): string => {
  return `${cubicMeters.toFixed(1)} m³`
}

// Note: MapboxGL Draw CSS should be imported in the consuming application
// import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'

// Custom draw styles
const drawStyles = [
  // Line styles
  {
    id: 'gl-draw-line',
    type: 'line',
    filter: ['all', ['==', '$type', 'LineString'], ['!=', 'mode', 'static']],
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
    paint: {
      'line-color': '#00ade6',
      'line-width': 3,
    },
  },
  // Polygon fill
  {
    id: 'gl-draw-polygon-fill',
    type: 'fill',
    filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
    paint: {
      'fill-color': '#00ade6',
      'fill-opacity': 0.1,
    },
  },
  // Polygon stroke
  {
    id: 'gl-draw-polygon-stroke-active',
    type: 'line',
    filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
    paint: {
      'line-color': '#00ade6',
      'line-width': 3,
    },
  },
  // Vertex points
  {
    id: 'gl-draw-polygon-and-line-vertex-active',
    type: 'circle',
    filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point']],
    paint: {
      'circle-radius': 5,
      'circle-color': '#00ade6',
    },
  },
]

export const MapDraw: React.FC<MapDrawProps> = ({
  map,
  enabled = false,
  onDrawingComplete,
  onDrawingUpdate,
  onDrawingDelete,
  onToolChange,
  initialData = [],
  drawingToolsProps = {},
  className,
}) => {
  const { t } = useTranslation('ui')
  const drawRef = useRef<MapboxDraw | null>(null)
  const currentModeRef = useRef<DrawingMode>('none')
  const [isToolsPanelOpen, setIsToolsPanelOpen] = useState(false)
  const [activeTool, setActiveTool] = useState<DrawingTool>('none')
  const [currentMode, setCurrentMode] = useState<DrawingMode>('none')
  const [showHeightModal, setShowHeightModal] = useState(false)
  const [pendingPolygon, setPendingPolygon] = useState<any>(null)
  const [drawings, setDrawings] = useState<DrawingData[]>(initialData)
  const hasInitializedFromProps = useRef(initialData.length > 0)
  const [selectedDrawingIds, setSelectedDrawingIds] = useState<string[]>([])
  const [editing3DId, setEditing3DId] = useState<string | null>(null)
  const [measurementLabels, setMeasurementLabels] = useState<
    Map<string, string[]>
  >(new Map())
  const measurementLabelsRef = useRef<Map<string, string[]>>(new Map())

  const [areaVolumeLabels, setAreaVolumeLabels] = useState<Map<string, string>>(
    new Map()
  )
  const areaVolumeLabelsRef = useRef<Map<string, string>>(new Map())

  // Refs for callbacks and drawings to avoid tearing down MapboxDraw when they change
  const onDrawingCompleteRef = useRef(onDrawingComplete)
  const onDrawingUpdateRef = useRef(onDrawingUpdate)
  const onDrawingDeleteRef = useRef(onDrawingDelete)
  const drawingsRef = useRef<DrawingData[]>(drawings)

  useEffect(() => {
    onDrawingCompleteRef.current = onDrawingComplete
  }, [onDrawingComplete])
  useEffect(() => {
    onDrawingUpdateRef.current = onDrawingUpdate
  }, [onDrawingUpdate])
  useEffect(() => {
    onDrawingDeleteRef.current = onDrawingDelete
  }, [onDrawingDelete])
  useEffect(() => {
    drawingsRef.current = drawings
  }, [drawings])

  const completedDrawingIds = useMemo(
    () => new Set(drawings.map((d) => d.id)),
    [drawings]
  )

  useDrawMeasurement({
    map,
    drawRef,
    currentModeRef,
    currentMode,
    enabled,
    completedDrawingIds,
  })

  useMeasureDistance({ map, currentMode, enabled })

  // Keep refs in sync with state
  useEffect(() => {
    measurementLabelsRef.current = measurementLabels
  }, [measurementLabels])

  useEffect(() => {
    areaVolumeLabelsRef.current = areaVolumeLabels
  }, [areaVolumeLabels])

  // Sync initialData when it arrives after mount (e.g. from API)
  useEffect(() => {
    if (!hasInitializedFromProps.current && initialData.length > 0) {
      hasInitializedFromProps.current = true
      setDrawings(initialData)
    }
  }, [initialData])

  // Function to add measurement labels for polygon/line segments
  const addMeasurementLabels = useCallback(
    (
      featureId: string,
      coordinates: number[][],
      isPolygon: boolean = false
    ) => {
      if (!map) return

      // Remove existing labels for this feature first by direct map access
      setMeasurementLabels((prevLabels) => {
        const existingLabelIds = prevLabels.get(featureId)
        if (existingLabelIds) {
          existingLabelIds.forEach((labelId) => {
            if (map.getLayer(labelId)) {
              map.removeLayer(labelId)
            }
            if (map.getSource(labelId)) {
              map.removeSource(labelId)
            }
          })
        }

        const labelIds: string[] = []
        // Build segments defensively: tolerates malformed/mismatched geometry
        // (e.g. a drawing persisted as type "polygon" but with a LineString
        // geometry) instead of feeding a bare number into calculateDistance.
        const segments = getMeasurementSegments(coordinates, isPolygon)

        segments.forEach(([coord1, coord2], i) => {
          const distance = calculateDistance(coord1, coord2)
          const midpoint = calculateMidpoint(coord1, coord2)
          const labelText = formatDistance(distance)

          const labelId = `measurement-${featureId}-${i}`
          labelIds.push(labelId)

          // Add source for the label
          try {
            map.addSource(labelId, {
              type: 'geojson',
              data: {
                type: 'Point',
                coordinates: midpoint,
              },
            })

            // Add label layer
            map.addLayer({
              id: labelId,
              type: 'symbol',
              source: labelId,
              layout: {
                'text-field': labelText,
                'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
                'text-size': 12,
                'text-anchor': 'center',
                'text-offset': [0, -0.5],
              },
              paint: {
                'text-color': '#ffffff',
                'text-halo-color': '#00ade6',
                'text-halo-width': 2,
              },
            })
          } catch (error) {
            console.warn(`Failed to add measurement label ${labelId}:`, error)
          }
        })

        // Return updated labels map
        return new Map(prevLabels.set(featureId, labelIds))
      })
    },
    [map]
  )

  // Function to remove measurement labels
  const removeMeasurementLabels = useCallback(
    (featureId: string) => {
      if (!map) return

      setMeasurementLabels((prevLabels) => {
        const labelIds = prevLabels.get(featureId)
        if (labelIds) {
          labelIds.forEach((labelId) => {
            if (map.getLayer(labelId)) {
              map.removeLayer(labelId)
            }
            if (map.getSource(labelId)) {
              map.removeSource(labelId)
            }
          })

          const newMap = new Map(prevLabels)
          newMap.delete(featureId)
          return newMap
        }
        return prevLabels
      })
    },
    [map]
  )

  // Function to add area/volume labels in polygon center
  const addAreaVolumeLabel = useCallback(
    (featureId: string, coordinates: [number, number][], height?: number) => {
      if (!map || coordinates.length < 3) return

      // Remove existing area/volume label
      setAreaVolumeLabels((prevLabels) => {
        const existingLabelId = prevLabels.get(featureId)
        if (existingLabelId) {
          if (map.getLayer(existingLabelId)) {
            map.removeLayer(existingLabelId)
          }
          if (map.getSource(existingLabelId)) {
            map.removeSource(existingLabelId)
          }
        }

        const centroid = calculatePolygonCentroid(coordinates)
        const area = calculatePolygonArea(coordinates)

        let labelText: string
        if (height !== undefined && height > 0) {
          // 3D polygon - show volume
          const volume = area * height
          labelText = formatVolume(volume)
        } else {
          // Regular polygon - show area
          labelText = formatArea(area)
        }

        const labelId = `area-volume-${featureId}`

        try {
          // Add source for the area/volume label
          map.addSource(labelId, {
            type: 'geojson',
            data: {
              type: 'Point',
              coordinates: centroid,
            },
          })

          // Add area/volume label layer
          map.addLayer({
            id: labelId,
            type: 'symbol',
            source: labelId,
            layout: {
              'text-field': labelText,
              'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
              'text-size': 14,
              'text-anchor': 'center',
            },
            paint: {
              'text-color': '#ffffff',
              'text-halo-color': '#00ade6',
              'text-halo-width': 1.5,
              'text-opacity': 0.9,
            },
          })

          // Return updated labels map
          return new Map(prevLabels.set(featureId, labelId))
        } catch (error) {
          console.warn(`Failed to add area/volume label ${labelId}:`, error)
          return prevLabels
        }
      })
    },
    [map]
  )

  // Function to remove area/volume labels
  const removeAreaVolumeLabel = useCallback(
    (featureId: string) => {
      if (!map) return

      setAreaVolumeLabels((prevLabels) => {
        const labelId = prevLabels.get(featureId)
        if (labelId) {
          if (map.getLayer(labelId)) {
            map.removeLayer(labelId)
          }
          if (map.getSource(labelId)) {
            map.removeSource(labelId)
          }

          const newMap = new Map(prevLabels)
          newMap.delete(featureId)
          return newMap
        }
        return prevLabels
      })
    },
    [map]
  )

  // Initialize mapbox-gl-draw
  useEffect(() => {
    if (!map || !enabled) return

    // Check if MapboxDraw is already initialized
    if (drawRef.current) return

    // Check if the sources already exist (from previous instances)
    const existingSources = ['mapbox-gl-draw-cold', 'mapbox-gl-draw-hot']

    existingSources.forEach((sourceId) => {
      if (map.getSource(sourceId)) {
        try {
          map.removeSource(sourceId)
        } catch (error) {
          console.warn(`Could not remove existing source ${sourceId}:`, error)
        }
      }
    })

    // Also check for existing layers
    const existingLayers = [
      'gl-draw-polygon-fill-inactive',
      'gl-draw-polygon-fill-active',
      'gl-draw-polygon-midpoint',
      'gl-draw-polygon-stroke-inactive',
      'gl-draw-polygon-stroke-active',
      'gl-draw-line-inactive',
      'gl-draw-line-active',
      'gl-draw-point-point-stroke-inactive',
      'gl-draw-point-point-stroke-active',
      'gl-draw-point-inactive',
      'gl-draw-point-active',
      'gl-draw-point-stroke-inactive',
      'gl-draw-point-stroke-active',
    ]

    existingLayers.forEach((layerId) => {
      if (map.getLayer(layerId)) {
        try {
          map.removeLayer(layerId)
        } catch (error) {
          console.warn(`Could not remove existing layer ${layerId}:`, error)
        }
      }
    })

    drawRef.current = new MapboxDraw({
      displayControlsDefault: false,
      styles: drawStyles,
      userProperties: true,
    })

    try {
      map.addControl(drawRef.current)
    } catch (error) {
      console.error('Failed to add MapboxDraw control:', error)
      drawRef.current = null
      return
    }

    // Event handlers
    const onCreate = (e: any) => {
      const feature = e.features[0]
      const mode = currentModeRef.current

      if (mode === '3d' && feature.geometry.type === 'Polygon') {
        // For 3D mode, show height input modal
        setPendingPolygon(feature)
        setShowHeightModal(true)
      } else {
        // For line and polygon modes, complete immediately
        const drawingData: DrawingData = {
          id: feature.id,
          type: mode as DrawingTool,
          geometry: feature.geometry,
          properties: feature.properties || {},
        }

        setDrawings((prev) => [...prev, drawingData])
        onDrawingCompleteRef.current?.(drawingData)

        // Add measurement labels for polygons and lines
        if (mode === 'polygon') {
          addMeasurementLabels(
            feature.id,
            feature.geometry.coordinates[0],
            true
          )
          // Add area label for polygons
          addAreaVolumeLabel(feature.id, feature.geometry.coordinates[0])
        } else if (mode === 'line') {
          addMeasurementLabels(feature.id, feature.geometry.coordinates, false)
        }

        // Keep the drawing mode active to allow drawing multiple shapes
        if (mode === 'polygon' || mode === 'line') {
          setTimeout(() => {
            if (!drawRef.current) return

            // Re-activate the same drawing mode
            if (mode === 'polygon') {
              drawRef.current.changeMode('draw_polygon')
            } else if (mode === 'line') {
              drawRef.current.changeMode('draw_line_string')
            }
          }, 100)
        }
      }
    }

    const onSelectionChange = (_e: any) => {
      const selected = drawRef.current?.getSelected()
      if (selected) {
        setSelectedDrawingIds(selected.features.map((f: any) => f.id))
      } else {
        setSelectedDrawingIds([])
      }
    }

    const onUpdate = (e: any) => {
      const feature = e.features[0]

      // Use ref to get current drawings (avoids stale closure)
      const existing = drawingsRef.current.find((d) => d.id === feature.id)
      const type = existing?.type || currentModeRef.current

      const drawingData: DrawingData = {
        id: feature.id,
        type: type as DrawingTool,
        geometry: feature.geometry,
        properties: existing?.properties || feature.properties || {},
      }

      setDrawings((prev) =>
        prev.map((d) => (d.id === drawingData.id ? drawingData : d))
      )
      onDrawingUpdateRef.current?.(drawingData)

      // Update measurement labels for polygons and lines
      if (type === 'polygon' || type === '3d') {
        addMeasurementLabels(feature.id, feature.geometry.coordinates[0], true)
        // Update area/volume label for polygons
        if (type === 'polygon') {
          addAreaVolumeLabel(feature.id, feature.geometry.coordinates[0])
        } else if (type === '3d') {
          const height = drawingData.properties?.height || 0
          addAreaVolumeLabel(
            feature.id,
            feature.geometry.coordinates[0],
            height
          )
        }
      } else if (type === 'line') {
        addMeasurementLabels(feature.id, feature.geometry.coordinates, false)
      }

      // If it's a 3D polygon, update the 3D layer
      if (type === '3d' && drawingData.properties?.height) {
        update3DPolygon(drawingData)
      }
    }

    const onDelete = (e: any) => {
      const deletedIds = e.features.map((f: any) => f.id)

      // Remove measurement labels for deleted features
      deletedIds.forEach((id: string) => {
        removeMeasurementLabels(id)
        removeAreaVolumeLabel(id)
        onDrawingDeleteRef.current?.(id)
      })

      setDrawings((prev) => prev.filter((d) => !deletedIds.includes(d.id)))
    }

    map.on('draw.create', onCreate)
    map.on('draw.update', onUpdate)
    map.on('draw.delete', onDelete)
    map.on('draw.selectionchange', onSelectionChange)

    return () => {
      map.off('draw.create', onCreate)
      map.off('draw.update', onUpdate)
      map.off('draw.delete', onDelete)
      map.off('draw.selectionchange', onSelectionChange)

      if (drawRef.current && map.hasControl(drawRef.current)) {
        try {
          // Clear terrain before removing control to prevent _updateTerrain
          // crash when Mapbox internally removes draw sources
          map.setTerrain(null)
        } catch {
          // ignore
        }
        try {
          map.removeControl(drawRef.current)
        } catch (error) {
          console.warn('Error removing MapboxDraw control:', error)
        }
      }
      drawRef.current = null
    }
  }, [map, enabled])

  const update3DPolygon = useCallback(
    (drawingData: DrawingData) => {
      if (!map || !drawingData.properties?.height) return

      const layerId = `3d-polygon-${drawingData.id}`
      const sourceId = `${layerId}-source`

      // Update the source data
      const source = map.getSource(sourceId) as any
      if (source) {
        source.setData({
          type: 'Feature',
          geometry: drawingData.geometry,
          properties: drawingData.properties,
        })
      }
    },
    [map]
  )

  const create3DPolygon = useCallback(
    (drawingData: DrawingData) => {
      if (!map || !drawingData.properties?.height) return

      const layerId = `3d-polygon-${drawingData.id}`
      const sourceId = `${layerId}-source`

      // Remove existing layer and source if they exist
      if (map.getLayer(layerId)) {
        map.removeLayer(layerId)
      }
      if (map.getSource(sourceId)) {
        map.removeSource(sourceId)
      }

      // Add source
      map.addSource(sourceId, {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: drawingData.geometry,
          properties: drawingData.properties,
        },
      })

      // Add 3D fill-extrusion layer
      map.addLayer({
        id: layerId,
        type: 'fill-extrusion',
        source: sourceId,
        paint: {
          'fill-extrusion-color': drawingData.properties?.color || '#00ade6',
          'fill-extrusion-height': drawingData.properties.height,
          'fill-extrusion-opacity': 0.8,
        },
      })

      // Add click handler for editing
      map.on('click', layerId, (e) => {
        e.preventDefault()
        e.originalEvent.stopPropagation()

        // Convert 3D polygon to editable 2D polygon
        if (drawRef.current) {
          // Add the polygon to MapboxDraw for editing
          const feature = {
            id: drawingData.id,
            type: 'Feature' as const,
            geometry: drawingData.geometry,
            properties: drawingData.properties || {},
          }

          // Hide 3D layer and measurement labels temporarily
          if (map.getLayer(layerId)) {
            map.setLayoutProperty(layerId, 'visibility', 'none')
          }
          removeMeasurementLabels(drawingData.id)
          removeAreaVolumeLabel(drawingData.id)

          // Add to MapboxDraw if not already there
          const existing = drawRef.current.get(drawingData.id)
          if (!existing) {
            drawRef.current.add(feature)
          }

          // Select it for editing
          drawRef.current.changeMode('direct_select', {
            featureId: drawingData.id,
          })
          setEditing3DId(drawingData.id)
        }
      })

      // Add double-click handler to edit height
      map.on('dblclick', layerId, (e) => {
        e.preventDefault()
        e.originalEvent.stopPropagation()

        // Show height modal to edit the height
        setPendingPolygon({
          id: drawingData.id,
          geometry: drawingData.geometry,
          properties: drawingData.properties,
          isEditing: true,
        })
        setShowHeightModal(true)
      })

      // Change cursor on hover
      map.on('mouseenter', layerId, () => {
        map.getCanvas().style.cursor = 'pointer'
      })

      map.on('mouseleave', layerId, () => {
        map.getCanvas().style.cursor = ''
      })

      // Switch to 3D view if not already
      if (map.getPitch() === 0) {
        map.easeTo({
          pitch: 45,
          bearing: 0,
          duration: 1000,
        })
      }
    },
    [map, update3DPolygon]
  )

  // Load initial data and sync drawings with MapboxDraw
  useEffect(() => {
    if (!drawRef.current || !map) return

    // Get current features in MapboxDraw
    const currentFeatures = drawRef.current.getAll()
    const currentIds = new Set(currentFeatures.features.map((f: any) => f.id))

    // Add any drawings that aren't already in MapboxDraw
    drawings.forEach((data) => {
      if (data.type === '3d') {
        // Create or update 3D visualization only if layer doesn't exist
        const layerId = `3d-polygon-${data.id}`
        if (!map.getLayer(layerId)) {
          create3DPolygon(data)
        }
        // Add measurement labels for 3D polygons only if they don't exist
        if (
          data.geometry.coordinates &&
          data.geometry.coordinates[0] &&
          !measurementLabelsRef.current.has(data.id)
        ) {
          addMeasurementLabels(data.id, data.geometry.coordinates[0], true)
        }
        // Add volume label for 3D polygons only if it doesn't exist
        if (
          data.geometry.coordinates &&
          data.geometry.coordinates[0] &&
          !areaVolumeLabelsRef.current.has(data.id)
        ) {
          const height = data.properties?.height || 0
          addAreaVolumeLabel(data.id, data.geometry.coordinates[0], height)
        }
        // Don't add to MapboxDraw unless editing
        if (editing3DId !== data.id && currentIds.has(data.id)) {
          // Remove from MapboxDraw if it's there but not being edited
          drawRef.current?.delete(data.id)
        }
      } else if (!currentIds.has(data.id)) {
        // For non-3D drawings, add directly to mapbox-draw
        const feature = {
          id: data.id,
          type: 'Feature' as const,
          geometry: data.geometry,
          properties: data.properties || {},
        }
        drawRef.current?.add(feature)

        // Add measurement labels for polygons and lines only if they don't exist
        if (!measurementLabelsRef.current.has(data.id)) {
          if (
            data.type === 'polygon' &&
            data.geometry.coordinates &&
            data.geometry.coordinates[0]
          ) {
            addMeasurementLabels(data.id, data.geometry.coordinates[0], true)
          } else if (data.type === 'line' && data.geometry.coordinates) {
            addMeasurementLabels(data.id, data.geometry.coordinates, false)
          }
        }
        // Add area label for polygons only if it doesn't exist
        if (
          data.type === 'polygon' &&
          data.geometry.coordinates &&
          data.geometry.coordinates[0] &&
          !areaVolumeLabelsRef.current.has(data.id)
        ) {
          addAreaVolumeLabel(data.id, data.geometry.coordinates[0])
        }
      }
    })
  }, [
    drawings,
    map,
    create3DPolygon,
    editing3DId,
    addMeasurementLabels,
    addAreaVolumeLabel,
  ])

  // Key handlers for delete and finish editing
  useEffect(() => {
    if (!enabled || !map) return

    const handleKeyPress = (e: KeyboardEvent) => {
      // ESC to delete selected drawings
      if (e.key === 'Escape' && selectedDrawingIds.length > 0 && !editing3DId) {
        e.preventDefault()

        // Delete selected drawings
        selectedDrawingIds.forEach((id) => {
          // Delete from mapbox-draw
          drawRef.current?.delete(id)

          // Remove measurement labels
          removeMeasurementLabels(id)
          removeAreaVolumeLabel(id)

          // Delete 3D layers if it's a 3D polygon
          const drawing = drawings.find((d) => d.id === id)
          if (drawing?.type === '3d') {
            const layerId = `3d-polygon-${id}`
            if (map.getLayer(layerId)) {
              map.removeLayer(layerId)
              if (map.getSource(`${layerId}-source`)) {
                map.removeSource(`${layerId}-source`)
              }
            }
          }
        })

        // Update drawings state
        setDrawings((prev) =>
          prev.filter((d) => !selectedDrawingIds.includes(d.id))
        )

        // Notify parent component
        selectedDrawingIds.forEach((id) => onDrawingDeleteRef.current?.(id))

        // Clear selection
        setSelectedDrawingIds([])
      }

      // Enter or ESC to finish editing 3D polygon
      if ((e.key === 'Enter' || e.key === 'Escape') && editing3DId) {
        e.preventDefault()

        const drawing = drawings.find((d) => d.id === editing3DId)
        if (drawing?.type === '3d') {
          // Remove from MapboxDraw
          drawRef.current?.delete(editing3DId)
          // Show 3D layer again
          const layerId = `3d-polygon-${editing3DId}`
          if (map.getLayer(layerId)) {
            map.setLayoutProperty(layerId, 'visibility', 'visible')
          }
          // Restore measurement labels and volume label
          if (drawing.geometry.coordinates && drawing.geometry.coordinates[0]) {
            addMeasurementLabels(
              editing3DId,
              drawing.geometry.coordinates[0],
              true
            )
            const height = drawing.properties?.height || 0
            addAreaVolumeLabel(
              editing3DId,
              drawing.geometry.coordinates[0],
              height
            )
          }
        }
        setEditing3DId(null)

        // Go back to simple select mode
        drawRef.current?.changeMode('simple_select')
      }
    }

    document.addEventListener('keydown', handleKeyPress)
    return () => document.removeEventListener('keydown', handleKeyPress)
  }, [
    enabled,
    map,
    selectedDrawingIds,
    drawings,
    editing3DId,
    removeMeasurementLabels,
  ])

  const handleToolSelect = useCallback(
    (tool: DrawingTool) => {
      if (!drawRef.current || !map) return

      setActiveTool(tool)
      setCurrentMode(tool as DrawingMode)
      currentModeRef.current = tool as DrawingMode

      // Notify parent component of tool change
      onToolChange?.(tool)

      // Clear selection when changing tools
      setSelectedDrawingIds([])

      // If we were editing a 3D polygon, restore its 3D visualization
      if (editing3DId) {
        const drawing = drawings.find((d) => d.id === editing3DId)
        if (drawing?.type === '3d') {
          // Remove from MapboxDraw
          drawRef.current.delete(editing3DId)
          // Show 3D layer again
          const layerId = `3d-polygon-${editing3DId}`
          if (map.getLayer(layerId)) {
            map.setLayoutProperty(layerId, 'visibility', 'visible')
          }
          // Restore measurement labels and volume label
          if (drawing.geometry.coordinates && drawing.geometry.coordinates[0]) {
            addMeasurementLabels(
              editing3DId,
              drawing.geometry.coordinates[0],
              true
            )
            const height = drawing.properties?.height || 0
            addAreaVolumeLabel(
              editing3DId,
              drawing.geometry.coordinates[0],
              height
            )
          }
        }
        setEditing3DId(null)
      }

      // Add a small delay to ensure the state is updated
      setTimeout(() => {
        if (!drawRef.current) return

        try {
          // Don't delete existing drawings, just change the mode
          switch (tool) {
            case 'line':
              drawRef.current.changeMode('draw_line_string')
              break
            case 'polygon':
            case '3d':
              drawRef.current.changeMode('draw_polygon')
              break
            case 'measure':
              // Measurement tool doesn't use MapboxDraw, switch to simple_select
              drawRef.current.changeMode('simple_select')
              break
            case 'none':
              drawRef.current.changeMode('simple_select')
              break
          }
        } catch (error) {
          console.error('Error changing drawing mode:', error)
        }
      }, 100)
    },
    [map, editing3DId, drawings, addMeasurementLabels, onToolChange]
  )

  const handleDeleteAll = useCallback(() => {
    if (!drawRef.current) return

    // Only delete if there are selected drawings, otherwise delete all
    if (selectedDrawingIds.length > 0) {
      // Delete only selected drawings
      selectedDrawingIds.forEach((id) => {
        drawRef.current?.delete(id)

        // Remove measurement labels
        removeMeasurementLabels(id)
        removeAreaVolumeLabel(id)

        // Delete 3D layers if it's a 3D polygon
        const drawing = drawings.find((d) => d.id === id)
        if (drawing?.type === '3d') {
          const layerId = `3d-polygon-${id}`
          if (map?.getLayer(layerId)) {
            map.removeLayer(layerId)
            if (map.getSource(`${layerId}-source`)) {
              map.removeSource(`${layerId}-source`)
            }
          }
        }
      })

      // Update drawings state
      setDrawings((prev) =>
        prev.filter((d) => !selectedDrawingIds.includes(d.id))
      )

      // Notify parent component for persistence
      selectedDrawingIds.forEach((id) => onDrawingDeleteRef.current?.(id))

      // Clear selection
      setSelectedDrawingIds([])
    } else {
      // Delete all drawings if nothing is selected
      const all = drawRef.current.getAll()
      if (all.features.length > 0) {
        drawRef.current.deleteAll()
        setDrawings([])
      }

      // Remove all measurement labels and notify parent for persistence
      drawings.forEach((drawing) => {
        removeMeasurementLabels(drawing.id)
        removeAreaVolumeLabel(drawing.id)
        onDrawingDeleteRef.current?.(drawing.id)
      })

      // Also remove all custom 3D layers
      drawings.forEach((drawing) => {
        if (drawing.type === '3d') {
          const layerId = `3d-polygon-${drawing.id}`
          if (map?.getLayer(layerId)) {
            map.removeLayer(layerId)
            if (map.getSource(`${layerId}-source`)) {
              map.removeSource(`${layerId}-source`)
            }
          }
        }
      })
    }
  }, [drawings, map, selectedDrawingIds, removeMeasurementLabels])

  const handleHeightConfirm = useCallback(
    (height: number) => {
      if (!pendingPolygon || !map) return

      const drawingData: DrawingData = {
        id: pendingPolygon.id,
        type: '3d',
        geometry: pendingPolygon.geometry,
        properties: {
          ...pendingPolygon.properties,
          height,
        },
      }

      if (pendingPolygon.isEditing) {
        // Update existing 3D polygon
        setDrawings((prev) =>
          prev.map((d) => (d.id === drawingData.id ? drawingData : d))
        )
        update3DPolygon(drawingData)
        // Update volume label
        addAreaVolumeLabel(
          drawingData.id,
          drawingData.geometry.coordinates[0],
          height
        )
        onDrawingUpdateRef.current?.(drawingData)
      } else {
        // Create new 3D polygon
        // Remove the 2D polygon from mapbox-draw
        drawRef.current?.delete(pendingPolygon.id)

        // Create 3D extrusion
        create3DPolygon(drawingData)

        // Add volume label
        addAreaVolumeLabel(
          drawingData.id,
          drawingData.geometry.coordinates[0],
          height
        )

        setDrawings((prev) => [...prev, drawingData])
        onDrawingCompleteRef.current?.(drawingData)

        // Keep 3D drawing mode active to allow drawing multiple 3D polygons
        setTimeout(() => {
          if (!drawRef.current) return
          drawRef.current.changeMode('draw_polygon')
        }, 100)
      }

      // Reset state
      setPendingPolygon(null)
      setShowHeightModal(false)
    },
    [pendingPolygon, map, create3DPolygon, update3DPolygon]
  )

  const handleHeightCancel = useCallback(() => {
    if (pendingPolygon && drawRef.current) {
      // Remove the incomplete polygon
      drawRef.current.delete(pendingPolygon.id)
    }

    setPendingPolygon(null)
    setShowHeightModal(false)
    handleToolSelect('none')
  }, [pendingPolygon])

  const customTools = [
    {
      id: 'line' as DrawingTool,
      label: t('drawingTools.drawLine'),
      icon: (
        <svg className="icon" viewBox="0 0 24 24">
          <path
            d="M4 20L20 4"
            stroke="currentColor"
            strokeWidth="2"
            fill="none"
          />
        </svg>
      ),
    },
    {
      id: 'polygon' as DrawingTool,
      label: t('drawingTools.drawPolygon'),
      icon: (
        <svg className="icon" viewBox="0 0 24 24">
          <path
            d="M12 2L2 7l10 5 10-5-10-5z"
            stroke="currentColor"
            strokeWidth="2"
            fill="none"
          />
        </svg>
      ),
    },
    {
      id: '3d' as DrawingTool,
      label: t('drawingTools.3dMode'),
      icon: (
        <svg className="icon" viewBox="0 0 24 24">
          <path
            d="M12 2L2 9v11l10 7 10-7V9L12 2z"
            stroke="currentColor"
            strokeWidth="2"
            fill="none"
          />
          <path d="M12 22V12" stroke="currentColor" strokeWidth="2" />
          <path d="M12 12L2 9" stroke="currentColor" strokeWidth="2" />
          <path d="M12 12l10-3" stroke="currentColor" strokeWidth="2" />
        </svg>
      ),
    },
    {
      id: 'measure' as DrawingTool,
      label: t('drawingTools.measureDistance'),
      icon: <Icon name="ruler" size="small" />,
    },
  ]

  if (!enabled) return null

  return (
    <div className={className}>
      <DrawingTools
        open={isToolsPanelOpen}
        onToggle={() => setIsToolsPanelOpen(!isToolsPanelOpen)}
        activeTool={activeTool}
        onToolSelect={handleToolSelect}
        onDeleteAll={handleDeleteAll}
        deleteButtonLabel={
          selectedDrawingIds.length > 0
            ? t('drawingTools.deleteSelected', {
                count: selectedDrawingIds.length,
              })
            : t('drawingTools.deleteAll')
        }
        tools={customTools}
        {...drawingToolsProps}
      />

      <HeightInputModal
        isOpen={showHeightModal}
        onConfirm={handleHeightConfirm}
        onCancel={handleHeightCancel}
        defaultHeight={pendingPolygon?.properties?.height || 10}
      />
    </div>
  )
}
